/**
 * Token-bound monograph content lists.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React from "react";
import { cva } from "class-variance-authority";
import { AlertTriangle, CheckCircle2, Circle, type LucideIcon } from "lucide-react";
import { motion, useReducedMotion } from "motion/react";
import { cn } from "../styling/index.js";
import { colorWithAlpha } from "./MonographPanelColor.js";
import type {
  MonographContentListProps,
  MonographContentListVariant,
  MonographPanelTokens,
} from "./MonographPanelPrimitiveTypes.js";

const contentListClass = cva("csk-monograph-content-list", {
  variants: {
    variant: {
      check: "csk-monograph-content-list--check",
      ordered: "csk-monograph-content-list--ordered",
      plain: "csk-monograph-content-list--plain",
      warn: "csk-monograph-content-list--warn",
    },
  },
  defaultVariants: { variant: "plain" },
});

const variantIcon: Readonly<Record<Exclude<MonographContentListVariant, "ordered">, LucideIcon>> = {
  check: CheckCircle2,
  plain: Circle,
  warn: AlertTriangle,
};

interface ListFactoryInput {
  readonly tokens: MonographPanelTokens;
}

function itemMotion(
  index: number,
  reducedMotion: boolean,
): React.ComponentProps<typeof motion.div> {
  return reducedMotion
    ? { initial: false }
    : {
        animate: { opacity: 1, y: 0 },
        initial: { opacity: 0, y: 4 },
        transition: { delay: index * 0.018, duration: 0.14 },
      };
}

function markerFor(
  variant: MonographContentListVariant,
  index: number,
  color: string,
): React.JSX.Element {
  if (variant === "ordered") {
    return (
      <span
        aria-hidden="true"
        style={{
          color,
          fontFamily: "inherit",
          fontSize: 11,
          fontWeight: 800,
        }}
      >
        {index + 1}
      </span>
    );
  }
  const Icon = variantIcon[variant];
  return <Icon aria-hidden="true" color={color} size={15} />;
}

export function createMonographPanelLists({ tokens }: ListFactoryInput): {
  readonly ContentList: (props: MonographContentListProps) => React.JSX.Element;
} {
  function ContentList({
    items,
    ariaLabel = "Content list",
    variant = "plain",
    dense = false,
    className,
  }: MonographContentListProps): React.JSX.Element {
    const reducedMotion = useReducedMotion() ?? false;
    return (
      <div
        role="list"
        aria-label={ariaLabel}
        className={cn(contentListClass({ variant }), className)}
        style={{ display: "grid", gap: dense ? 8 : 10 }}
      >
        {items.map((item, index) => {
          const color =
            item.color ??
            (variant === "check"
              ? (tokens.accentGreen ?? tokens.accentSafe ?? tokens.accent)
              : variant === "warn"
                ? (tokens.accentRed ?? tokens.accentForbidden ?? "#ef4444")
                : tokens.accent);
          return (
            <motion.div
              key={item.id}
              role="listitem"
              {...itemMotion(index, reducedMotion)}
              style={{
                alignItems: "flex-start",
                background: colorWithAlpha(color, dense ? "0a" : "0d"),
                border: `1px solid ${colorWithAlpha(color, dense ? "24" : "30")}`,
                borderLeft: `3px solid ${color}`,
                borderRadius: 11,
                display: "grid",
                gap: 10,
                gridTemplateColumns: "28px minmax(0, 1fr)",
                padding: dense ? "9px 11px" : "11px 13px",
              }}
            >
              <span
                style={{
                  alignItems: "center",
                  background: colorWithAlpha(color, "18"),
                  border: `1px solid ${colorWithAlpha(color, "44")}`,
                  borderRadius: 8,
                  display: "inline-flex",
                  flexShrink: 0,
                  height: 28,
                  justifyContent: "center",
                  marginTop: 1,
                  width: 28,
                }}
              >
                {markerFor(variant, index, color)}
              </span>
              <span style={{ minWidth: 0 }}>
                {(item.title !== undefined || item.meta !== undefined) && (
                  <span
                    style={{
                      alignItems: "baseline",
                      display: "flex",
                      flexWrap: "wrap",
                      gap: 8,
                      marginBottom: item.body ? 4 : 0,
                    }}
                  >
                    {item.title !== undefined && (
                      <strong style={{ color: tokens.bright, fontSize: 13.2 }}>{item.title}</strong>
                    )}
                    {item.meta !== undefined && (
                      <span style={{ color, fontFamily: tokens.mono, fontSize: 10.5 }}>
                        {item.meta}
                      </span>
                    )}
                  </span>
                )}
                <span
                  style={{ color: tokens.text, display: "block", fontSize: 12.8, lineHeight: 1.65 }}
                >
                  {item.body}
                </span>
              </span>
            </motion.div>
          );
        })}
      </div>
    );
  }

  return { ContentList };
}
